import React, { useEffect, useState } from 'react';
import AppContext from '../AppContext';
import { Redirect } from 'react-router-dom';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, Grid, Input, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import PaddedGrid from '../components/PaddedGrid';
import APIService from '../services/APIService';
import Loading from '../components/Loading';

const initialQuestions = {
    'question_1': { fi: '', en: '' },
    'question_2': { fi: '', en: '' },
    'question_3': { fi: '', en: '' },
};

const initialMessageQuestions = { fi: '', en: '' };

const FeedbackQuestions = ({ restaurantId = null, organisationId = null }) => {
    const { t } = useTranslation();
    const [questions, setQuestions] = useState(initialQuestions);
    const [messageQuestion, setMessageQuestion] = useState(initialMessageQuestions);
    const [errors, setErrors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState(false);

    const [changed, setChanged] = useState(false);
    const [originalQuestions, setOriginalQuestions] = useState(initialQuestions);
    const [originalMessageQuestion, setOriginalMessageQuestion] = useState(initialMessageQuestions);

    useEffect(() => {
        APIService.getFeedbackQuestions({ restaurantId, organisationId }).then((res) => {
            setQuestions(res.questions || initialQuestions);
            setMessageQuestion(res.message_question || initialMessageQuestions);
            setOriginalQuestions(res.questions || initialQuestions);
            setOriginalMessageQuestion(res.message_question || initialMessageQuestions);
            setLoading(false);
        }).catch((_error) => {
            AppContext.toast(t('Tapahtui odottamaton virhe'));
            setLoading(false);
        });
    }, []);

    const handleSave = () => {
        APIService.updateFeedbackQuestions({
            ...questions,
            message_question: messageQuestion,
            restaurantId,
            organisationId
        }).then((res) => {
            setQuestions(res.questions);
            setMessageQuestion(res.message_question);
            setOriginalQuestions(res.questions);
            setOriginalMessageQuestion(res.message_question);
            setChanged(false);
            setErrors([]);
            AppContext.toast(t('Tallennettu onnistuneesti'));
        }).catch((error) => {
            setErrors(error.message);
            setChanged(true);
        });
    };

    const handleDelete = () => {
        APIService.deleteFeedbackQuestions({ restaurantId, organisationId }).then(() => {
            setQuestions(initialQuestions);
            setMessageQuestion(initialMessageQuestions);
            setOriginalQuestions(initialQuestions);
            setOriginalMessageQuestion(initialMessageQuestions);
            setChanged(false);
            setDialog(false);
            AppContext.toast(t('Kysymykset poistettu onnistuneesti'));
        }).catch((_error) => {
            AppContext.toast(t('Tapahtui odottamaton virhe'));
            setDialog(false);
            setChanged(true);
        });
    };

    const handleReset = () => {
        setQuestions(originalQuestions);
        setMessageQuestion(originalMessageQuestion);
        setChanged(false);
    };

    const handleChange = (event, lang) => {
        const { value, name } = event.target;

        setQuestions((prevState) => ({
            ...prevState,
            [name]: {
                ...prevState[name],
                [lang]: value || null,
            }
        }));

        setChanged(true);
    }

    const handleMessageQuestionChange = (event, lang) => {
        const { value } = event.target;

        setMessageQuestion((prevState) => ({
            ...prevState,
            [lang]: value || null,
        }));

        setChanged(true);
    }

    if (!AppContext.isAdmin() && !AppContext.isOrganisation()) {
        return (<Redirect to="/" />);
    }

    if (loading) {
        return <Loading />;
    }

    return (
        <PaddedGrid>
            <Grid item xs={12}>
                <Typography variant="title">{t('Palautekysymykset')}</Typography>
            </Grid>
            <Grid item xs={12}>
                <FormControl fullWidth>
                    <Typography variant="subheading" style={{ paddingLeft: 24 }}>{t('Monivalinta')}</Typography>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ border: 'none' }}>{t('Suomeksi')}</TableCell>
                                <TableCell style={{ border: 'none' }}>{t('Englanniksi')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Object.entries(questions).map((entry) => {
                                const [key, values] = entry;

                                return (
                                    <TableRow key={key}>
                                        <TableCell style={{ border: 'none' }}>
                                            <TextField
                                                fullWidth
                                                name={key}
                                                value={values.fi}
                                                onChange={(event) => handleChange(event, 'fi')}
                                                error={errors.includes(`${key}.fi`)}
                                            />
                                        </TableCell>
                                        <TableCell style={{ border: 'none' }}>
                                            <TextField
                                                fullWidth
                                                name={key}
                                                value={values.en}
                                                onChange={(event) => handleChange(event, 'en')}
                                                error={errors.includes(`${key}.en`)}
                                            />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                    <Typography variant="subheading" style={{ paddingLeft: 24, marginTop: '2rem' }}>{t('Tekstikenttä')}</Typography>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ border: 'none' }}>{t('Suomeksi')}</TableCell>
                                <TableCell style={{ border: 'none' }}>{t('Englanniksi')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell style={{ border: 'none' }}>
                                    <TextField
                                        fullWidth
                                        value={messageQuestion.fi}
                                        onChange={(event) => handleMessageQuestionChange(event, 'fi')}
                                        error={errors.includes('message_question.fi')}
                                    />
                                </TableCell>
                                <TableCell style={{ border: 'none' }}>
                                    <TextField
                                        fullWidth
                                        value={messageQuestion.en}
                                        onChange={(event) => handleMessageQuestionChange(event, 'en')}
                                        error={errors.includes('message_question.en')}
                                    />
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                    <Grid container spacing={24} style={{ marginTop: '2rem', gap: '2rem', justifyContent: 'flex-end', paddingRight: '36px', paddingLeft: '36px' }}>
                        {(restaurantId || organisationId) && (JSON.stringify(originalQuestions) !== JSON.stringify(initialQuestions)) && (JSON.stringify(originalMessageQuestion) !== JSON.stringify(initialMessageQuestions)) && (
                            <Button variant="raised" color="secondary" onClick={() => setDialog(true)} style={{ background: 'red', color: 'white' }}>
                                {t('Poista')}
                            </Button>
                        )}
                        <Button variant="raised" color="secondary" onClick={handleReset} disabled={!changed}>
                            {t('Palauta')}
                        </Button>
                        <Button variant="raised" color="primary" onClick={handleSave}>
                            {t('Tallenna')}
                        </Button>
                    </Grid>
                </FormControl>
            </Grid>
            <Dialog
                open={dialog}
                onClose={() => setDialog(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{t('Oletko varma')}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {t('Haluatko varmasti poistaa tämän?')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialog(false)} color="primary">
                        {t('En')}
                    </Button>
                    <Button onClick={handleDelete} color="primary" autoFocus>
                        {t('Kyllä')}
                    </Button>
                </DialogActions>
            </Dialog>
        </PaddedGrid>
    );
};

export default (FeedbackQuestions);



// WEBPACK FOOTER //
// ./resources/assets/js/containers/FeedbackQuestions.js